import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { NotificationTextMessage } from '@/core/enums/notification';
import type { ReportLogoInfo } from '@/core/models/firm-settings.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { practiceService } from '@/core/services/practice';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';

const MAX_LOGO_BYTES = 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg'];

/**
 * Edit Client > Upload Logo (QA rows 218 and 221): a client's own logo. Mount it with `key={clientId}` so another client starts clean. It prints on that client's accounts and in its report
 * emails in place of the firm's logo (Settings > Logo on Report); a client without one keeps the firm's. PNG or JPG, 1 MB or less -
 * the limits the API enforces. The file is saved as soon as it is chosen, like the firm's logo, not with the client form.
 */
export function ClientLogoField({ clientId }: { clientId: string }) {
  const [logo, setLogo] = useState<ReportLogoInfo | null>(null);
  /** Data URL of the uploaded logo. */
  const [preview, setPreview] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const mounted = useRef(true);

  const loadPreview = () => {
    practiceService.getClientLogoImage(clientId).then(
      (blob) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (mounted.current) setPreview(typeof reader.result === 'string' ? reader.result : null);
        };
        reader.readAsDataURL(blob);
      },
      () => {
        if (mounted.current) setPreview(null);
      },
    );
  };

  const apply = (info: ReportLogoInfo) => {
    setLogo(info);
    if (info.hasLogo) {
      loadPreview();
    } else {
      setPreview(null);
    }
  };

  useEffect(() => {
    mounted.current = true;
    practiceService.getClientLogo(clientId).then(
      (info) => {
        if (mounted.current) apply(info);
      },
      (error) => notifyError(error),
    );
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;
    input.value = '';
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      notifyError(NotificationTextMessage.fileJpgPng);
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      notifyError('File should be less than 1 MB');
      return;
    }

    setIsSaving(true);
    practiceService.uploadClientLogo(clientId, file).then(
      (info) => {
        setIsSaving(false);
        if (mounted.current) apply(info);
        notifySuccess('Client logo uploaded');
      },
      (error) => {
        setIsSaving(false);
        notifyError(error);
      },
    );
  };

  const onDelete = () => {
    setIsSaving(true);
    practiceService.deleteClientLogo(clientId).then(
      (info) => {
        setIsSaving(false);
        if (mounted.current) apply(info);
        notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
      },
      (error) => {
        setIsSaving(false);
        notifyError(error);
      },
    );
  };

  return (
    <div className="form-field d-block mb-1">
      <p className="mb-1">Logo</p>

      <div className="d-flex align-items-center gap-15 mb-1">
        {preview && (
          <div className="logo-preview">
            <img src={preview} alt="Client logo" style={{ maxHeight: 80, maxWidth: 240 }} />
          </div>
        )}
        {!preview && (
          <div className="info-message">
            No logo uploaded for this client. Its reports and emails use the firm&rsquo;s logo, if there is one.
          </div>
        )}
      </div>

      <div className="d-flex align-items-center gap-10">
        <input
          type="file"
          ref={fileInput}
          id="clientLogoFile"
          className="d-none"
          accept="image/png,image/jpeg"
          onChange={onFileSelected}
        />
        <Button className="action-button primary-button" disabled={isSaving} onClick={() => fileInput.current?.click()}>
          <Icon name="upload" />
          <span>{logo?.hasLogo ? 'Replace Logo' : 'Upload Logo'}</span>
        </Button>
        {logo?.hasLogo && (
          <Button className="action-button danger-button" disabled={isSaving} onClick={onDelete}>
            <Icon name="delete_outline" outlined />
            <span>Delete Logo</span>
          </Button>
        )}
      </div>

      {logo?.hasLogo && (
        <p className="font-size-14 mt-10 mb-0">
          {logo.fileName} ({logo.sizeBytes} bytes)
        </p>
      )}
      <p className="font-size-14 mt-10 mb-0">PNG or JPG, 1 MB or less.</p>
    </div>
  );
}
